import { useState, useEffect, useCallback } from 'react';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../components/ui/resizable';
import { ScrollArea } from '../components/ui/scroll-area';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Plus, Search, Filter, Tag, Database, FileCode, Trash2, Edit, Copy,
  GitBranch, History, RotateCcw, Eye, Play, ChevronRight, ChevronDown,
  CheckCircle, XCircle, AlertCircle, Clock,
  Upload, Download, Settings, Shield, Loader2, X, Sparkles, Zap,
  FlaskConical, Archive, Check, RefreshCw, GitCompare, PlayCircle, AlertTriangle
} from 'lucide-react';
import CodeMirror from '@uiw/react-codemirror';
import { sql } from '@codemirror/lang-sql';
import { oneDark } from '@codemirror/theme-one-dark';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued';
import { cn } from '../lib/utils';
import {
  getFewShots, createFewShot, updateFewShot, deleteFewShot,
  getFewShotVersions, getFewShotVersionDiff, restoreFewShotVersion, testFewShotRetrieval,
  bulkImportFewShots, bulkExportFewShots,
  validateAllFewShots, validateFewShot
} from '../lib/api';

// Status badge colors
const statusColors = {
  active: 'bg-green-500/20 text-green-400 border-green-500/30',
  deprecated: 'bg-red-500/20 text-red-400 border-red-500/30',
  experimental: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  draft: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
};

const statusIcons = {
  active: CheckCircle,
  deprecated: XCircle,
  experimental: FlaskConical,
  draft: Clock
};

// SQL Dialect options
const sqlDialects = [
  { value: 'databricks', label: 'Databricks SQL' },
  { value: 'snowflake', label: 'Snowflake SQL' },
  { value: 'standard', label: 'Standard SQL' }
];

export default function FewShots() {
  // State management
  const [fewShots, setFewShots] = useState([]);
  const [selectedFewShot, setSelectedFewShot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTags, setFilterTags] = useState([]);
  const [filterDomain, setFilterDomain] = useState('all');

  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showTestRetrieval, setShowTestRetrieval] = useState(false);
  const [showImportExport, setShowImportExport] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showValidationResults, setShowValidationResults] = useState(false);

  // Validation state
  const [validationResults, setValidationResults] = useState(null);
  const [validating, setValidating] = useState(false);

  // Form state for create/edit
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    question: '',
    sqlVariants: [{ dialect: 'databricks', sql: '', isDefault: true }],
    domain: '',
    tags: [],
    status: 'draft',
    notes: ''
  });
  const [newTag, setNewTag] = useState('');

  // Version history
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);

  // Test retrieval
  const [testQuery, setTestQuery] = useState('');
  const [retrievalResults, setRetrievalResults] = useState(null);
  const [testingRetrieval, setTestingRetrieval] = useState(false);

  // Retrieval settings
  const [retrievalSettings, setRetrievalSettings] = useState({
    embeddingModel: 'text-embedding-3-small',
    topK: 5,
    similarityThreshold: 0.7,
    includeDeprecated: false,
    pinnedFewShots: []
  });

  // All unique tags and domains for filtering
  const [allTags, setAllTags] = useState([]);
  const [allDomains, setAllDomains] = useState([]);

  // Load few-shots
  const loadFewShots = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getFewShots({
        search: searchQuery,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        domain: filterDomain !== 'all' ? filterDomain : undefined,
        tags: filterTags.length > 0 ? filterTags.join(',') : undefined
      });
      setFewShots(res.data.few_shots || []);
      setAllTags(res.data.all_tags || []);
      setAllDomains(res.data.all_domains || []);
    } catch (err) {
      console.error('Failed to load few-shots:', err);
      toast.error('Failed to load few-shots');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filterStatus, filterDomain, filterTags]);

  useEffect(() => {
    loadFewShots();
  }, [loadFewShots]);

  // Create/Update few-shot
  const handleSaveFewShot = async () => {
    if (!formData.question.trim()) {
      toast.error('Question is required');
      return;
    }
    if (formData.sqlVariants.length === 0 || !formData.sqlVariants[0].sql.trim()) {
      toast.error('At least one SQL variant is required');
      return;
    }

    try {
      if (editMode && selectedFewShot) {
        await updateFewShot(selectedFewShot.id, formData);
        toast.success('Few-shot updated successfully');
      } else {
        await createFewShot(formData);
        toast.success('Few-shot created successfully');
      }
      setShowCreateDialog(false);
      resetForm();
      loadFewShots();
    } catch (err) {
      toast.error(editMode ? 'Failed to update few-shot' : 'Failed to create few-shot');
    }
  };

  // Delete few-shot (soft delete)
  const handleDeleteFewShot = async (id) => {
    if (!confirm('Are you sure you want to delete this few-shot?')) return;
    try {
      await deleteFewShot(id);
      toast.success('Few-shot deleted');
      if (selectedFewShot?.id === id) {
        setSelectedFewShot(null);
      }
      loadFewShots();
    } catch (err) {
      toast.error('Failed to delete few-shot');
    }
  };

  // Load version history
  const loadVersions = async (fewShotId) => {
    try {
      const res = await getFewShotVersions(fewShotId);
      setVersions(res.data);
    } catch (err) {
      toast.error('Failed to load version history');
    }
  };

  // Restore version
  const handleRestoreVersion = async (versionId) => {
    try {
      await restoreFewShotVersion(selectedFewShot.id, versionId);
      toast.success('Version restored successfully');
      loadFewShots();
      loadVersions(selectedFewShot.id);
    } catch (err) {
      toast.error('Failed to restore version');
    }
  };

  // Run all SQL validations
  const handleRunAllValidations = async () => {
    setValidating(true);
    setShowValidationResults(true);
    try {
      const res = await validateAllFewShots();
      setValidationResults(res.data);
      if (res.data.failed > 0) {
        toast.error(`${res.data.failed} few-shot(s) failed validation`);
      } else {
        toast.success(`All ${res.data.passed} few-shots passed validation`);
      }
    } catch (err) {
      toast.error('Failed to run validations');
    } finally {
      setValidating(false);
    }
  };

  // Test retrieval
  const handleTestRetrieval = async () => {
    if (!testQuery.trim()) {
      toast.error('Please enter a test query');
      return;
    }
    setTestingRetrieval(true);
    try {
      const res = await testFewShotRetrieval({
        query: testQuery,
        settings: retrievalSettings
      });
      setRetrievalResults(res.data);
    } catch (err) {
      toast.error('Failed to test retrieval');
    } finally {
      setTestingRetrieval(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      question: '',
      sqlVariants: [{ dialect: 'databricks', sql: '', isDefault: true }],
      domain: '',
      tags: [],
      status: 'draft',
      notes: ''
    });
    setEditMode(false);
    setNewTag('');
  };

  // Open edit dialog
  const openEditDialog = (fewShot) => {
    setFormData({
      question: fewShot.question,
      sqlVariants: fewShot.sql_variants || [{ dialect: 'databricks', sql: fewShot.sql || '', isDefault: true }],
      domain: fewShot.domain || '',
      tags: fewShot.tags || [],
      status: fewShot.status || 'draft',
      notes: fewShot.notes || ''
    });
    setEditMode(true);
    setShowCreateDialog(true);
  };

  // Add SQL variant
  const addSqlVariant = () => {
    setFormData(prev => ({
      ...prev,
      sqlVariants: [...prev.sqlVariants, { dialect: 'snowflake', sql: '', isDefault: false }]
    }));
  };

  // Remove SQL variant
  const removeSqlVariant = (index) => {
    if (formData.sqlVariants.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      sqlVariants: prev.sqlVariants.filter((_, i) => i !== index)
    }));
  };

  // Update SQL variant
  const updateSqlVariant = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      sqlVariants: prev.sqlVariants.map((v, i) => 
        i === index ? { ...v, [field]: value } : v
      )
    }));
  };

  // Add tag
  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  // Remove tag
  const removeTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  // Filter few-shots
  const filteredFewShots = fewShots;

  return (
    <div className="h-full flex flex-col bg-[#030712]" data-testid="few-shots-page">
      {/* Header */}
      <div className="h-12 border-b border-[#1E293B] flex items-center justify-between px-4 bg-[#0F172A]">
        <div className="flex items-center gap-4">
          <h2 className="text-sm font-medium text-[#F1F5F9]">Few-Shot Management</h2>
          <Badge variant="outline" className="text-xs">
            {fewShots.length} few-shots
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRunAllValidations}
            disabled={validating || fewShots.length === 0}
            className="text-[#64748B] hover:text-white"
            data-testid="run-all-btn"
          >
            {validating ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <PlayCircle className="w-4 h-4 mr-1" />
            )}
            Run All
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowImportExport(true)}
            className="text-[#64748B] hover:text-white"
            data-testid="import-export-btn"
          >
            <Upload className="w-4 h-4 mr-1" />
            Import/Export
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowSettings(true)}
            className="text-[#64748B] hover:text-white"
            data-testid="settings-btn"
          >
            <Settings className="w-4 h-4 mr-1" />
            Settings
          </Button>
          <Button
            size="sm"
            onClick={() => { resetForm(); setShowCreateDialog(true); }}
            className="bg-[#0EA5E9] hover:bg-[#0284C7]"
            data-testid="create-few-shot-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            New Few-Shot
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        <ResizablePanelGroup direction="horizontal">
          {/* Left Panel - List & Filters */}
          <ResizablePanel defaultSize={35} minSize={25} maxSize={50}>
            <div className="h-full flex flex-col bg-[#0F172A] border-r border-[#1E293B]">
              {/* Search & Filters */}
              <div className="p-3 border-b border-[#1E293B] space-y-3">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                  <Input
                    placeholder="Search questions or SQL..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 bg-[#030712] border-[#1E293B]"
                    data-testid="search-few-shots"
                  />
                </div>
                <div className="flex gap-2">
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="flex-1 h-8 text-xs bg-[#030712] border-[#1E293B]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="experimental">Experimental</SelectItem>
                      <SelectItem value="deprecated">Deprecated</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterDomain} onValueChange={setFilterDomain}>
                    <SelectTrigger className="flex-1 h-8 text-xs bg-[#030712] border-[#1E293B]">
                      <SelectValue placeholder="Domain" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Domains</SelectItem>
                      {allDomains.map(domain => (
                        <SelectItem key={domain} value={domain}>{domain}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {allTags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {allTags.slice(0, 10).map(tag => (
                      <Badge
                        key={tag}
                        variant="outline"
                        className={cn(
                          "text-[10px] cursor-pointer transition-colors",
                          filterTags.includes(tag) 
                            ? "bg-[#0EA5E9]/20 text-[#0EA5E9] border-[#0EA5E9]/30" 
                            : "text-[#64748B] hover:text-[#F1F5F9]"
                        )}
                        onClick={() => {
                          setFilterTags(prev => 
                            prev.includes(tag) 
                              ? prev.filter(t => t !== tag)
                              : [...prev, tag]
                          );
                        }}
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Few-Shot List */}
              <ScrollArea className="flex-1">
                <div className="p-2 space-y-1">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-[#0EA5E9]" />
                    </div>
                  ) : filteredFewShots.length === 0 ? (
                    <div className="text-center py-8 text-[#64748B]">
                      <Database className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No few-shots found</p>
                      <p className="text-xs mt-1">Create your first few-shot pair</p>
                    </div>
                  ) : (
                    filteredFewShots.map(fs => {
                      const StatusIcon = statusIcons[fs.status] || Clock;
                      return (
                        <div
                          key={fs.id}
                          className={cn(
                            "p-3 rounded-md cursor-pointer transition-all border",
                            selectedFewShot?.id === fs.id
                              ? "bg-[#0EA5E9]/10 border-[#0EA5E9]/30"
                              : "bg-[#030712] border-[#1E293B] hover:border-[#334155]"
                          )}
                          onClick={() => setSelectedFewShot(fs)}
                          data-testid={`few-shot-item-${fs.id}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm text-[#F1F5F9] line-clamp-2 flex-1">
                              {fs.question}
                            </p>
                            <Badge 
                              variant="outline" 
                              className={cn("text-[10px] shrink-0", statusColors[fs.status])}
                            >
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {fs.status}
                            </Badge>
                          </div>
                          <div className="mt-2 flex items-center gap-2 flex-wrap">
                            {fs.domain && (
                              <Badge variant="outline" className="text-[10px] text-[#7C3AED]">
                                {fs.domain}
                              </Badge>
                            )}
                            {fs.tags?.slice(0, 3).map(tag => (
                              <Badge key={tag} variant="outline" className="text-[10px] text-[#64748B]">
                                {tag}
                              </Badge>
                            ))}
                            {fs.tags?.length > 3 && (
                              <span className="text-[10px] text-[#64748B]">
                                +{fs.tags.length - 3}
                              </span>
                            )}
                          </div>
                          <div className="mt-2 flex items-center gap-3 text-[10px] text-[#64748B]">
                            <span className="flex items-center gap-1">
                              <FileCode className="w-3 h-3" />
                              {fs.sql_variants?.length || 1} variant{(fs.sql_variants?.length || 1) > 1 ? 's' : ''}
                            </span>
                            <span className="flex items-center gap-1">
                              <GitBranch className="w-3 h-3" />
                              v{fs.version || 1}
                            </span>
                            {fs.retrieval_count > 0 && (
                              <span className="flex items-center gap-1">
                                <Eye className="w-3 h-3" />
                                {fs.retrieval_count} uses
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </ScrollArea>

              {/* Test Retrieval Button */}
              <div className="p-3 border-t border-[#1E293B]">
                <Button
                  variant="outline"
                  className="w-full border-[#1E293B]"
                  onClick={() => setShowTestRetrieval(true)}
                  data-testid="test-retrieval-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Test Semantic Retrieval
                </Button>
              </div>
            </div>
          </ResizablePanel>

          <ResizableHandle />

          {/* Right Panel - Detail View */}
          <ResizablePanel defaultSize={65} minSize={50}>
            {selectedFewShot ? (
              <FewShotDetailView
                fewShot={selectedFewShot}
                onEdit={() => openEditDialog(selectedFewShot)}
                onDelete={() => handleDeleteFewShot(selectedFewShot.id)}
                onViewHistory={() => {
                  loadVersions(selectedFewShot.id);
                  setShowVersionHistory(true);
                }}
                onDuplicate={() => {
                  setFormData({
                    question: selectedFewShot.question + ' (Copy)',
                    sqlVariants: selectedFewShot.sql_variants || [{ dialect: 'databricks', sql: selectedFewShot.sql || '', isDefault: true }],
                    domain: selectedFewShot.domain || '',
                    tags: selectedFewShot.tags || [],
                    status: 'draft',
                    notes: selectedFewShot.notes || ''
                  });
                  setEditMode(false);
                  setShowCreateDialog(true);
                }}
              />
            ) : (
              <div className="h-full flex items-center justify-center bg-[#030712]">
                <div className="text-center text-[#64748B]">
                  <Database className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p className="text-sm">Select a few-shot to view details</p>
                  <p className="text-xs mt-1">or create a new one</p>
                </div>
              </div>
            )}
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-[#0F172A] border-[#1E293B]">
          <DialogHeader>
            <DialogTitle className="text-[#F1F5F9]">
              {editMode ? 'Edit Few-Shot' : 'Create New Few-Shot'}
            </DialogTitle>
            <DialogDescription className="text-[#64748B]">
              Define a natural language question and its corresponding SQL queries
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Question */}
            <div className="space-y-2">
              <Label className="text-[#F1F5F9]">Natural Language Question</Label>
              <Textarea
                value={formData.question}
                onChange={(e) => setFormData(prev => ({ ...prev, question: e.target.value }))}
                placeholder="e.g., What are the top 10 customers by revenue last month?"
                className="bg-[#030712] border-[#1E293B] min-h-[80px]"
                data-testid="few-shot-question-input"
              />
            </div>

            {/* SQL Variants */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-[#F1F5F9]">SQL Variants</Label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={addSqlVariant}
                  className="border-[#1E293B]"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Variant
                </Button>
              </div>
              {formData.sqlVariants.map((variant, index) => (
                <div key={index} className="p-3 bg-[#030712] rounded-md border border-[#1E293B] space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Select
                        value={variant.dialect}
                        onValueChange={(value) => updateSqlVariant(index, 'dialect', value)}
                      >
                        <SelectTrigger className="w-[160px] h-8 text-xs bg-[#0F172A] border-[#1E293B]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {sqlDialects.map(d => (
                            <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {variant.isDefault && (
                        <Badge className="bg-[#0EA5E9]/20 text-[#0EA5E9] text-[10px]">Default</Badge>
                      )}
                    </div>
                    {formData.sqlVariants.length > 1 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeSqlVariant(index)}
                        className="h-8 w-8 p-0 text-[#64748B] hover:text-red-400"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <CodeMirror
                    value={variant.sql}
                    height="120px"
                    theme={oneDark}
                    extensions={[sql()]}
                    onChange={(value) => updateSqlVariant(index, 'sql', value)}
                    className="rounded-md overflow-hidden border border-[#1E293B]"
                    data-testid={`sql-variant-${index}`}
                  />
                </div>
              ))}
            </div>

            {/* Domain & Tags */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#F1F5F9]">Domain</Label>
                <Input
                  value={formData.domain}
                  onChange={(e) => setFormData(prev => ({ ...prev, domain: e.target.value }))}
                  placeholder="e.g., Sales, Marketing, Finance"
                  className="bg-[#030712] border-[#1E293B]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#F1F5F9]">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger className="bg-[#030712] border-[#1E293B]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="experimental">Experimental</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="deprecated">Deprecated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Tags */}
            <div className="space-y-2">
              <Label className="text-[#F1F5F9]">Tags</Label>
              <div className="flex gap-2">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Add a tag..."
                  className="bg-[#030712] border-[#1E293B]"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                />
                <Button variant="outline" onClick={addTag} className="border-[#1E293B]">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.tags.map(tag => (
                    <Badge
                      key={tag}
                      variant="outline"
                      className="text-xs cursor-pointer hover:bg-red-500/20"
                      onClick={() => removeTag(tag)}
                    >
                      {tag}
                      <X className="w-3 h-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-[#F1F5F9]">Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional context or usage notes..."
                className="bg-[#030712] border-[#1E293B]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveFewShot} className="bg-[#0EA5E9] hover:bg-[#0284C7]">
              {editMode ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version History Dialog with Diffs */}
      <Dialog open={showVersionHistory} onOpenChange={setShowVersionHistory}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-[#0F172A] border-[#1E293B]">
          <DialogHeader>
            <DialogTitle className="text-[#F1F5F9] flex items-center gap-2">
              <History className="w-5 h-5" />
              Version History with Visual Diffs
            </DialogTitle>
          </DialogHeader>
          <VersionHistoryWithDiff 
            versions={versions} 
            fewShotId={selectedFewShot?.id}
            onRestore={handleRestoreVersion}
          />
        </DialogContent>
      </Dialog>

      {/* Test Retrieval Dialog */}
      <Dialog open={showTestRetrieval} onOpenChange={setShowTestRetrieval}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto bg-[#0F172A] border-[#1E293B]">
          <DialogHeader>
            <DialogTitle className="text-[#F1F5F9] flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#0EA5E9]" />
              Semantic Retrieval Debugger
            </DialogTitle>
            <DialogDescription className="text-[#64748B]">
              Test how few-shots are retrieved for a given query
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {/* Test Query Input */}
            <div className="space-y-2">
              <Label className="text-[#F1F5F9]">Test Query</Label>
              <div className="flex gap-2">
                <Textarea
                  value={testQuery}
                  onChange={(e) => setTestQuery(e.target.value)}
                  placeholder="Enter a natural language query to test retrieval..."
                  className="bg-[#030712] border-[#1E293B] min-h-[60px]"
                />
                <Button
                  onClick={handleTestRetrieval}
                  disabled={testingRetrieval}
                  className="bg-[#0EA5E9] hover:bg-[#0284C7] shrink-0"
                >
                  {testingRetrieval ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* Retrieval Settings */}
            <div className="p-3 bg-[#030712] rounded-md border border-[#1E293B]">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-[#64748B]">Top-K:</Label>
                  <Input
                    type="number"
                    value={retrievalSettings.topK}
                    onChange={(e) => setRetrievalSettings(prev => ({ ...prev, topK: parseInt(e.target.value) }))}
                    className="w-16 h-8 text-xs bg-[#0F172A] border-[#1E293B]"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-[#64748B]">Threshold:</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="1"
                    value={retrievalSettings.similarityThreshold}
                    onChange={(e) => setRetrievalSettings(prev => ({ ...prev, similarityThreshold: parseFloat(e.target.value) }))}
                    className="w-20 h-8 text-xs bg-[#0F172A] border-[#1E293B]"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={retrievalSettings.includeDeprecated}
                    onChange={(e) => setRetrievalSettings(prev => ({ ...prev, includeDeprecated: e.target.checked }))}
                    className="rounded"
                  />
                  <Label className="text-xs text-[#64748B]">Include Deprecated</Label>
                </div>
              </div>
            </div>

            {/* Retrieval Results */}
            {retrievalResults && (
              <div className="space-y-3">
                <Label className="text-[#F1F5F9]">Retrieved Few-Shots ({retrievalResults.results?.length || 0})</Label>
                {retrievalResults.results?.map((result, index) => (
                  <div
                    key={result.id}
                    className="p-3 bg-[#030712] rounded-md border border-[#1E293B]"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-[10px]">#{index + 1}</Badge>
                          <span className="text-xs text-[#0EA5E9] font-mono">
                            Score: {(result.similarity_score * 100).toFixed(1)}%
                          </span>
                          {result.is_pinned && (
                            <Badge className="bg-yellow-500/20 text-yellow-400 text-[10px]">Pinned</Badge>
                          )}
                        </div>
                        <p className="text-sm text-[#F1F5F9]">{result.question}</p>
                        {result.matched_phrases && (
                          <p className="text-xs text-[#64748B] mt-1">
                            Matched: {result.matched_phrases.join(', ')}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <Badge variant="outline" className={cn("text-[10px]", statusColors[result.status])}>
                          {result.status}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Import/Export Dialog */}
      <Dialog open={showImportExport} onOpenChange={setShowImportExport}>
        <DialogContent className="max-w-2xl bg-[#0F172A] border-[#1E293B]">
          <DialogHeader>
            <DialogTitle className="text-[#F1F5F9] flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Import / Export Few-Shots
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <Tabs defaultValue="import">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="import">Import</TabsTrigger>
                <TabsTrigger value="export">Export</TabsTrigger>
              </TabsList>
              <TabsContent value="import" className="space-y-4 mt-4">
                <div className="p-4 border-2 border-dashed border-[#1E293B] rounded-md text-center">
                  <Upload className="w-8 h-8 mx-auto mb-2 text-[#64748B]" />
                  <p className="text-sm text-[#64748B]">Drag & drop JSON/YAML file or click to browse</p>
                  <input type="file" className="hidden" accept=".json,.yaml,.yml" />
                  <Button variant="outline" className="mt-2">Browse Files</Button>
                </div>
              </TabsContent>
              <TabsContent value="export" className="space-y-4 mt-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#94A3B8]">Export Format</span>
                    <Select defaultValue="json">
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="json">JSON</SelectItem>
                        <SelectItem value="yaml">YAML</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#94A3B8]">Include</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs cursor-pointer">All</Badge>
                      <Badge variant="outline" className="text-xs cursor-pointer">Active Only</Badge>
                      <Badge variant="outline" className="text-xs cursor-pointer">Selected</Badge>
                    </div>
                  </div>
                  <Button className="w-full bg-[#0EA5E9] hover:bg-[#0284C7]">
                    <Download className="w-4 h-4 mr-2" />
                    Export Few-Shots
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-2xl bg-[#0F172A] border-[#1E293B]">
          <DialogHeader>
            <DialogTitle className="text-[#F1F5F9] flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Retrieval Settings
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-6">
            {/* Embedding Model */}
            <div className="space-y-2">
              <Label className="text-[#F1F5F9]">Embedding Model</Label>
              <Select
                value={retrievalSettings.embeddingModel}
                onValueChange={(v) => setRetrievalSettings(prev => ({ ...prev, embeddingModel: v }))}
              >
                <SelectTrigger className="bg-[#030712] border-[#1E293B]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text-embedding-3-small">OpenAI text-embedding-3-small</SelectItem>
                  <SelectItem value="text-embedding-3-large">OpenAI text-embedding-3-large</SelectItem>
                  <SelectItem value="text-embedding-ada-002">OpenAI text-embedding-ada-002</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Top-K */}
            <div className="space-y-2">
              <Label className="text-[#F1F5F9]">Default Top-K Results</Label>
              <Input
                type="number"
                value={retrievalSettings.topK}
                onChange={(e) => setRetrievalSettings(prev => ({ ...prev, topK: parseInt(e.target.value) }))}
                className="bg-[#030712] border-[#1E293B]"
              />
            </div>

            {/* Similarity Threshold */}
            <div className="space-y-2">
              <Label className="text-[#F1F5F9]">Similarity Threshold</Label>
              <Input
                type="number"
                step="0.05"
                min="0"
                max="1"
                value={retrievalSettings.similarityThreshold}
                onChange={(e) => setRetrievalSettings(prev => ({ ...prev, similarityThreshold: parseFloat(e.target.value) }))}
                className="bg-[#030712] border-[#1E293B]"
              />
              <p className="text-xs text-[#64748B]">Minimum similarity score (0-1) to include in results</p>
            </div>

            {/* Include Deprecated */}
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-[#F1F5F9]">Include Deprecated</Label>
                <p className="text-xs text-[#64748B]">Include deprecated few-shots in retrieval</p>
              </div>
              <input
                type="checkbox"
                checked={retrievalSettings.includeDeprecated}
                onChange={(e) => setRetrievalSettings(prev => ({ ...prev, includeDeprecated: e.target.checked }))}
                className="rounded w-5 h-5"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>Cancel</Button>
            <Button className="bg-[#0EA5E9] hover:bg-[#0284C7]" onClick={() => {
              toast.success('Settings saved');
              setShowSettings(false);
            }}>Save Settings</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Validation Results Dialog */}
      <Dialog open={showValidationResults} onOpenChange={setShowValidationResults}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto bg-[#0F172A] border-[#1E293B]">
          <DialogHeader>
            <DialogTitle className="text-[#F1F5F9] flex items-center gap-2">
              <PlayCircle className="w-5 h-5 text-[#0EA5E9]" />
              SQL Validation Results
            </DialogTitle>
            <DialogDescription className="text-[#64748B]">
              Results of running SQL validation on all few-shots
            </DialogDescription>
          </DialogHeader>

          {validating ? (
            <div className="py-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-[#0EA5E9]" />
              <p className="text-[#94A3B8]">Running validations...</p>
            </div>
          ) : validationResults ? (
            <div className="py-4 space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-[#030712] rounded-md border border-[#1E293B] text-center">
                  <div className="text-2xl font-bold text-[#F1F5F9]">{validationResults.total}</div>
                  <div className="text-xs text-[#64748B]">Total Queries</div>
                </div>
                <div className="p-4 bg-[#030712] rounded-md border border-green-500/30 text-center">
                  <div className="text-2xl font-bold text-green-400">{validationResults.passed}</div>
                  <div className="text-xs text-[#64748B]">Passed</div>
                </div>
                <div className="p-4 bg-[#030712] rounded-md border border-red-500/30 text-center">
                  <div className="text-2xl font-bold text-red-400">{validationResults.failed}</div>
                  <div className="text-xs text-[#64748B]">Failed</div>
                </div>
              </div>

              {/* Results List - Failed First */}
              <div className="space-y-3">
                {validationResults.failed > 0 && (
                  <Label className="text-red-400 flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    Failed Validations ({validationResults.failed})
                  </Label>
                )}
                
                {validationResults.results?.filter(r => !r.passed).map((result) => (
                  <div
                    key={result.id}
                    className="p-4 bg-[#030712] rounded-md border border-red-500/30"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <XCircle className="w-4 h-4 text-red-400" />
                          <span className="text-sm text-[#F1F5F9] font-medium">{result.question}</span>
                        </div>
                        {result.domain && (
                          <Badge variant="outline" className="text-[10px] text-[#7C3AED] mr-2">
                            {result.domain}
                          </Badge>
                        )}
                        <Badge variant="outline" className={cn("text-[10px]", statusColors[result.status])}>
                          {result.status}
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Show failed variants */}
                    {result.variants?.filter(v => !v.passed).map((variant, idx) => (
                      <div key={idx} className="mt-3 p-3 bg-red-500/5 rounded border border-red-500/20">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="text-[10px]">{variant.dialect}</Badge>
                          <span className="text-xs text-red-400 font-mono">Error</span>
                        </div>
                        <p className="text-sm text-red-300 mb-2">{variant.error}</p>
                        <div className="bg-[#0F172A] rounded p-2 overflow-x-auto">
                          <pre className="text-xs text-[#94A3B8] whitespace-pre-wrap">{variant.sql}</pre>
                        </div>
                        {variant.warnings?.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {variant.warnings.map((warning, wIdx) => (
                              <div key={wIdx} className="flex items-center gap-2 text-xs text-yellow-400">
                                <AlertTriangle className="w-3 h-3" />
                                {warning}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}

                {/* Passed queries */}
                {validationResults.passed > 0 && (
                  <>
                    <Label className="text-green-400 flex items-center gap-2 mt-6">
                      <CheckCircle className="w-4 h-4" />
                      Passed Validations ({validationResults.passed})
                    </Label>
                    
                    {validationResults.results?.filter(r => r.passed).map((result) => (
                      <div
                        key={result.id}
                        className="p-3 bg-[#030712] rounded-md border border-green-500/20"
                      >
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />
                          <span className="text-sm text-[#94A3B8] truncate">{result.question}</span>
                          <div className="flex items-center gap-1 ml-auto shrink-0">
                            {result.variants?.map((v, idx) => (
                              <Badge key={idx} variant="outline" className="text-[10px] text-green-400 border-green-500/30">
                                {v.dialect}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        {/* Show warnings if any */}
                        {result.variants?.some(v => v.warnings?.length > 0) && (
                          <div className="mt-2 space-y-1 ml-6">
                            {result.variants.flatMap((v, vIdx) => 
                              v.warnings?.map((warning, wIdx) => (
                                <div key={`${vIdx}-${wIdx}`} className="flex items-center gap-2 text-xs text-yellow-400">
                                  <AlertTriangle className="w-3 h-3" />
                                  <span className="text-[#64748B]">[{v.dialect}]</span> {warning}
                                </div>
                              )) || []
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="py-8 text-center text-[#64748B]">
              <p>No validation results yet</p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowValidationResults(false)}>
              Close
            </Button>
            <Button 
              onClick={handleRunAllValidations} 
              disabled={validating}
              className="bg-[#0EA5E9] hover:bg-[#0284C7]"
            >
              {validating ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-1" />
              )}
              Re-run All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Few-Shot Detail View Component
function FewShotDetailView({ fewShot, onEdit, onDelete, onViewHistory, onDuplicate }) {
  const [activeTab, setActiveTab] = useState('sql');
  const StatusIcon = statusIcons[fewShot.status] || Clock;

  return (
    <div className="h-full flex flex-col bg-[#030712]">
      {/* Header */}
      <div className="p-4 border-b border-[#1E293B] bg-[#0F172A]">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline" className={cn("text-xs", statusColors[fewShot.status])}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {fewShot.status}
              </Badge>
              {fewShot.domain && (
                <Badge variant="outline" className="text-xs text-[#7C3AED]">
                  {fewShot.domain}
                </Badge>
              )}
              <Badge variant="outline" className="text-xs text-[#64748B]">
                v{fewShot.version || 1}
              </Badge>
            </div>
            <h3 className="text-lg text-[#F1F5F9] leading-tight">{fewShot.question}</h3>
            {fewShot.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {fewShot.tags.map(tag => (
                  <Badge key={tag} variant="outline" className="text-[10px] text-[#64748B]">
                    <Tag className="w-3 h-3 mr-1" />
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={onViewHistory} data-testid="view-history-btn">
              <History className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onDuplicate} data-testid="duplicate-btn">
              <Copy className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onEdit} data-testid="edit-btn">
              <Edit className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onDelete} className="text-red-400 hover:text-red-300" data-testid="delete-btn">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <TabsList className="w-full justify-start rounded-none border-b border-[#1E293B] bg-transparent h-10 p-0">
          <TabsTrigger
            value="sql"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0EA5E9] data-[state=active]:bg-transparent"
          >
            <FileCode className="w-4 h-4 mr-1" />
            SQL Variants
          </TabsTrigger>
          <TabsTrigger
            value="metadata"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0EA5E9] data-[state=active]:bg-transparent"
          >
            <Database className="w-4 h-4 mr-1" />
            Metadata
          </TabsTrigger>
        </TabsList>

        <TabsContent value="sql" className="flex-1 overflow-auto p-4 m-0">
          <div className="space-y-4">
            {(fewShot.sql_variants || [{ dialect: 'databricks', sql: fewShot.sql || '', isDefault: true }]).map((variant, index) => (
              <div key={index} className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {sqlDialects.find(d => d.value === variant.dialect)?.label || variant.dialect}
                  </Badge>
                  {variant.isDefault && (
                    <Badge className="bg-[#0EA5E9]/20 text-[#0EA5E9] text-[10px]">Default</Badge>
                  )}
                </div>
                <div className="rounded-md overflow-hidden border border-[#1E293B]">
                  <CodeMirror
                    value={variant.sql}
                    height="auto"
                    minHeight="100px"
                    maxHeight="300px"
                    theme={oneDark}
                    extensions={[sql()]}
                    editable={false}
                    className="text-sm"
                  />
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="metadata" className="flex-1 overflow-auto p-4 m-0">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-[#0F172A] rounded-md border border-[#1E293B]">
                <Label className="text-xs text-[#64748B]">Created</Label>
                <p className="text-sm text-[#F1F5F9]">
                  {fewShot.created_at ? new Date(fewShot.created_at).toLocaleString() : 'N/A'}
                </p>
              </div>
              <div className="p-3 bg-[#0F172A] rounded-md border border-[#1E293B]">
                <Label className="text-xs text-[#64748B]">Last Updated</Label>
                <p className="text-sm text-[#F1F5F9]">
                  {fewShot.updated_at ? new Date(fewShot.updated_at).toLocaleString() : 'N/A'}
                </p>
              </div>
              <div className="p-3 bg-[#0F172A] rounded-md border border-[#1E293B]">
                <Label className="text-xs text-[#64748B]">Version</Label>
                <p className="text-sm text-[#F1F5F9]">v{fewShot.version || 1}</p>
              </div>
              <div className="p-3 bg-[#0F172A] rounded-md border border-[#1E293B]">
                <Label className="text-xs text-[#64748B]">Domain</Label>
                <p className="text-sm text-[#F1F5F9]">{fewShot.domain || 'None'}</p>
              </div>
            </div>
            {fewShot.notes && (
              <div className="p-3 bg-[#0F172A] rounded-md border border-[#1E293B]">
                <Label className="text-xs text-[#64748B]">Notes</Label>
                <p className="text-sm text-[#94A3B8] mt-1">{fewShot.notes}</p>
              </div>
            )}
            {fewShot.schema_version && (
              <div className="p-3 bg-[#0F172A] rounded-md border border-[#1E293B]">
                <Label className="text-xs text-[#64748B]">Associated Schema Version</Label>
                <p className="text-sm text-[#F1F5F9]">{fewShot.schema_version}</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Version History with Diff Component
function VersionHistoryWithDiff({ versions, fewShotId, onRestore }) {
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [diffData, setDiffData] = useState(null);
  const [loadingDiff, setLoadingDiff] = useState(false);

  // Custom diff styles for dark theme
  const diffStyles = {
    variables: {
      dark: {
        diffViewerBackground: '#030712',
        diffViewerColor: '#F1F5F9',
        addedBackground: '#10B98120',
        addedColor: '#10B981',
        removedBackground: '#EF444420',
        removedColor: '#EF4444',
        wordAddedBackground: '#10B98140',
        wordRemovedBackground: '#EF444440',
        addedGutterBackground: '#10B98130',
        removedGutterBackground: '#EF444430',
        gutterBackground: '#0F172A',
        gutterBackgroundDark: '#0F172A',
        highlightBackground: '#1E293B',
        highlightGutterBackground: '#1E293B',
        codeFoldGutterBackground: '#0F172A',
        codeFoldBackground: '#0F172A',
        emptyLineBackground: '#030712',
        gutterColor: '#64748B',
        addedGutterColor: '#10B981',
        removedGutterColor: '#EF4444',
        codeFoldContentColor: '#64748B',
        diffViewerTitleBackground: '#0F172A',
        diffViewerTitleColor: '#F1F5F9',
        diffViewerTitleBorderColor: '#1E293B',
      },
    },
    line: {
      padding: '4px 8px',
      fontSize: '12px',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    },
    contentText: {
      fontSize: '12px',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    }
  };

  const loadDiff = async (versionNum) => {
    setLoadingDiff(true);
    try {
      const res = await getFewShotVersionDiff(fewShotId, versionNum);
      setDiffData(res.data);
      setSelectedVersion(versionNum);
    } catch (err) {
      toast.error('Failed to load diff');
    } finally {
      setLoadingDiff(false);
    }
  };

  return (
    <div className="py-4 space-y-4">
      {versions.length === 0 ? (
        <p className="text-center text-[#64748B] py-8">No version history available</p>
      ) : (
        <div className="flex gap-4">
          {/* Version List */}
          <div className="w-64 shrink-0 space-y-2">
            <Label className="text-xs text-[#64748B] uppercase tracking-wide">Versions</Label>
            {versions.map((version, index) => (
              <div
                key={version.id}
                className={cn(
                  "p-3 rounded-md border cursor-pointer transition-colors",
                  selectedVersion === version.version 
                    ? "bg-[#0EA5E9]/20 border-[#0EA5E9]/50" 
                    : index === 0 
                      ? "bg-[#0EA5E9]/10 border-[#0EA5E9]/30 hover:bg-[#0EA5E9]/15" 
                      : "bg-[#030712] border-[#1E293B] hover:bg-[#0F172A]"
                )}
                onClick={() => loadDiff(version.version)}
              >
                <div className="flex items-center justify-between mb-1">
                  <Badge variant="outline" className="text-xs">v{version.version}</Badge>
                  {index === 0 && (
                    <Badge className="bg-[#0EA5E9]/20 text-[#0EA5E9] text-[10px]">Current</Badge>
                  )}
                </div>
                <p className="text-[10px] text-[#64748B]">
                  {new Date(version.created_at).toLocaleString()}
                </p>
                {version.change_note && (
                  <p className="text-[10px] text-[#94A3B8] mt-1 truncate">{version.change_note}</p>
                )}
                {index > 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => { e.stopPropagation(); onRestore(version.id); }}
                    className="h-6 text-[10px] mt-2 w-full"
                  >
                    <RotateCcw className="w-3 h-3 mr-1" />
                    Restore
                  </Button>
                )}
              </div>
            ))}
          </div>

          {/* Diff View */}
          <div className="flex-1 border border-[#1E293B] rounded-md overflow-hidden">
            {loadingDiff ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-6 h-6 animate-spin text-[#0EA5E9]" />
              </div>
            ) : diffData ? (
              <div className="space-y-4">
                {/* Question Diff */}
                <div className="border-b border-[#1E293B]">
                  <div className="px-4 py-2 bg-[#0F172A] border-b border-[#1E293B]">
                    <Label className="text-xs text-[#94A3B8] flex items-center gap-2">
                      <GitCompare className="w-3 h-3" />
                      Question - v{diffData.version} vs v{diffData.version - 1}
                    </Label>
                  </div>
                  {!diffData.has_previous ? (
                    <div className="p-4 bg-green-500/5">
                      <p className="text-xs text-[#64748B] mb-2">Initial version (no previous version to compare)</p>
                      <p className="text-sm text-green-400">{diffData.question?.new}</p>
                    </div>
                  ) : diffData.question?.old === diffData.question?.new ? (
                    <div className="p-4 text-center text-[#64748B] text-sm">
                      No changes in question
                    </div>
                  ) : (
                    <ReactDiffViewer
                      oldValue={diffData.question?.old || ''}
                      newValue={diffData.question?.new || ''}
                      splitView={false}
                      useDarkTheme={true}
                      styles={diffStyles}
                      compareMethod={DiffMethod.WORDS}
                      hideLineNumbers={true}
                    />
                  )}
                </div>

                {/* SQL Variants Diff */}
                <div>
                  <div className="px-4 py-2 bg-[#0F172A] border-b border-[#1E293B]">
                    <Label className="text-xs text-[#94A3B8] flex items-center gap-2">
                      <Database className="w-3 h-3" />
                      SQL Variants
                    </Label>
                  </div>
                  {!diffData.has_previous ? (
                    <div className="p-4 space-y-3">
                      <p className="text-xs text-[#64748B]">Initial version SQL variants:</p>
                      {diffData.sql_variants?.new?.map((variant, i) => (
                        <div key={i} className="bg-green-500/5 p-3 rounded border border-green-500/20">
                          <Badge variant="outline" className="mb-2 text-[10px]">{variant.dialect}</Badge>
                          <pre className="text-xs text-green-400 whitespace-pre-wrap font-mono">
                            {variant.sql_template}
                          </pre>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 space-y-4">
                      {diffData.sql_variants?.new?.map((newVariant, i) => {
                        const oldVariant = diffData.sql_variants?.old?.find(v => v.dialect === newVariant.dialect);
                        const oldSql = oldVariant?.sql_template || '';
                        const newSql = newVariant.sql_template || '';
                        
                        if (oldSql === newSql && oldVariant) {
                          return (
                            <div key={i} className="text-center text-[#64748B] text-xs py-2">
                              <Badge variant="outline" className="mr-2">{newVariant.dialect}</Badge>
                              No changes
                            </div>
                          );
                        }
                        
                        return (
                          <div key={i} className="border border-[#1E293B] rounded overflow-hidden">
                            <div className="px-3 py-1 bg-[#0F172A] flex items-center gap-2">
                              <Badge variant="outline" className="text-[10px]">{newVariant.dialect}</Badge>
                              {!oldVariant && <Badge className="bg-green-500/20 text-green-400 text-[10px]">New</Badge>}
                            </div>
                            <ReactDiffViewer
                              oldValue={oldSql}
                              newValue={newSql}
                              splitView={true}
                              useDarkTheme={true}
                              styles={diffStyles}
                              compareMethod={DiffMethod.LINES}
                              leftTitle="Previous"
                              rightTitle="Current"
                            />
                          </div>
                        );
                      })}
                      
                      {/* Show removed variants */}
                      {diffData.sql_variants?.old?.filter(oldV => 
                        !diffData.sql_variants?.new?.find(newV => newV.dialect === oldV.dialect)
                      ).map((removedVariant, i) => (
                        <div key={`removed-${i}`} className="border border-red-500/30 rounded overflow-hidden">
                          <div className="px-3 py-1 bg-red-500/10 flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px]">{removedVariant.dialect}</Badge>
                            <Badge className="bg-red-500/20 text-red-400 text-[10px]">Removed</Badge>
                          </div>
                          <pre className="p-3 text-xs text-red-400 whitespace-pre-wrap font-mono bg-red-500/5">
                            {removedVariant.sql_template}
                          </pre>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-[#64748B]">
                <GitCompare className="w-12 h-12 mb-4 opacity-30" />
                <p>Select a version to view diff</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
